/**
 * Charleye cart drawer: the round "+" on the suggestion cards.
 *
 * Adds the variant through the Ajax cart, asking Shopify to render the cart
 * drawer section in the same request, then announces the change with the
 * standard CartLinesUpdateEvent — the same event Horizon's own product form
 * sends. The drawer's cart-items component hears it and morphs in the new
 * markup, and the header cart count updates, with no extra wiring here.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ch-upsell-add]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  const variantId = button.dataset.variantId;
  if (!variantId) return;

  button.disabled = true;
  button.classList.add('is-loading');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append('sections', SECTION_ID);
    body.append('sections_url', window.location.pathname);

    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    }).then((res) => res.json());

    if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

    const cart = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`).then((res) => res.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'charleye-cart-upsell',
        sections: response.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Add to cart failed',
        code: 'INVALID',
      })
    );
    button.disabled = false;
    button.classList.remove('is-loading');
  }
});

/**
 * Suggestions carousel: drag with the mouse.
 *
 * Touch and trackpads already swipe the track natively (it scrolls sideways
 * with snap points). A mouse cannot, so a press-and-drag moves it instead,
 * then lets the snap settle on the nearest card. A drag never counts as a
 * click, so releasing over a card or its "+" after dragging does nothing.
 * Delegated from the document because the drawer's markup is replaced on
 * every cart update.
 */
let drag = null;

document.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  const track = event.target instanceof Element ? event.target.closest('.ch-upsell__track') : null;
  if (!track || track.scrollWidth <= track.clientWidth) return;
  drag = { track, startX: event.clientX, startScroll: track.scrollLeft, moved: false };
});

document.addEventListener('pointermove', (event) => {
  if (!drag) return;
  const dx = event.clientX - drag.startX;
  if (!drag.moved && Math.abs(dx) < 5) return;
  if (!drag.moved) {
    drag.moved = true;
    // Snapping fights a manual scroll; it comes back on release.
    drag.track.style.scrollSnapType = 'none';
    drag.track.classList.add('is-dragging');
  }
  drag.track.scrollLeft = drag.startScroll - dx;
});

const endDrag = () => {
  if (!drag) return;
  const { track, moved } = drag;
  drag = null;
  if (!moved) return;
  track.classList.remove('is-dragging');
  track.style.removeProperty('scroll-snap-type');
  // Swallow the click the browser fires right after this release (it comes
  // before any timer), then drop the guard so a later real click goes through.
  const swallow = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  track.addEventListener('click', swallow, { capture: true, once: true });
  setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
};

document.addEventListener('pointerup', endDrag);
document.addEventListener('pointercancel', endDrag);
